import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Image as ImageIcon,
  FileText,
  Globe,
  AlertTriangle,
  Shield,
  Clock,
  ExternalLink,
  Download,
  Eye,
  Code,
  Database,
  Cpu,
  Maximize2,
  Activity,
  Layers,
  Zap,
  Brain
} from 'lucide-react';
import { api } from '../utils/api';
import { formatSafeRelativeTime, formatDetailedTime, formatAbsoluteTime } from '../utils/date-formatter';
import { AnalysisSteps } from '../components/AnalysisSteps';
import { SourceBadge } from '../components/SourceBadge';
import { formatPostIdForDisplay } from '../utils/id-normalization';
import { DepthAnalysisModal } from '../components/DepthAnalysisModal';
import { STIXExport } from '../components/STIXExport';

interface ImageAnalysis {
  id: string;
  image_url: string;
  extracted_text?: string;
  detected_objects?: string;
  has_weapons: boolean;
  has_exploit_code: boolean;
  has_suspicious_text: boolean;
  has_personal_info: boolean;
  threat_score: number;
  width?: number;
  height?: number;
  file_size?: number;
  analyzed_at: string;
}

interface WebsiteCrawl {
  id: string;
  url: string;
  title?: string;
  content?: string;
  description?: string;
  images_count: number;
  links_count: number;
  crawled_at: string;
  response_time?: number;
  content_length?: number;
  status_code?: number;
  threat_score: number;
}

interface ThreatIndicator {
  indicator_type: string;
  indicator_value: string;
  confidence_score: number;
  verified: boolean;
  created_at: string;
}

interface StoredImage {
  type: string;
  url: string;
  analysis?: ImageAnalysis;
}

interface DetailedPost {
  id: string;
  username: string;
  content: string;
  created_at: string;
  analyzed_at?: string;
  url?: string;
  threat_level: string;
  summary?: string;
  analysis_id?: string;
  latest_alert_id?: string;
  source_feed?: string;
  website_crawl?: WebsiteCrawl;
  image_analysis?: ImageAnalysis[];
  threat_indicators?: ThreatIndicator[];
  stored_images?: StoredImage[];
  depth_max_level?: number;
  depth_analysis_complete?: boolean;
  depth_aggregated_threat_score?: number;
  ttps?: string[];
  iocs?: string[];
  recommendations?: string[];
  analyzed_steps?: {
    topic_relevance: number;
    threat_assessment: string;
    confidence_score: number;
    ai_model_used: string;
    analysis_timestamp: string;
  };
  analysis_type?: 'quick_assessment' | 'enhanced_analysis';
}

export function PostDetails() {
  // [DEBUG-TRAP] Support both /posts/:id and /analysis/:analysisId routes
  const params = useParams<{ analysisId?: string; id?: string }>();
  const analysisId = params.analysisId || params.id; // Use whichever is provided
  
  console.log('[DEBUG-TRAP] PostDetails mounted:', {
    routeParams: params,
    resolvedAnalysisId: analysisId,
    timestamp: new Date().toISOString()
  });
  
  const navigate = useNavigate();
  const [post, setPost] = useState<DetailedPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null  >(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'content' | 'images' | 'website' | 'indicators' | 'stix' | 'analysis'>('summary');
  const [analysisSteps, setAnalysisSteps] = useState<any[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [selectedDepthLevel, setSelectedDepthLevel] = useState<number | null>(0); // Default to L0

  console.log('PostDetails page rendered with analysisId:', analysisId);

  useEffect(() => {
    if (analysisId) {
      loadPostDetails();
      loadAnalysisSteps();
    }
  }, [analysisId]);

  const loadPostDetails = async () => {
    if (!analysisId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('Loading post details for analysis ID:', analysisId);
      const response = await api.getPostDetailsByAnalysisId(analysisId);
      console.log('Post details response:', response);
      console.log('Post content:', response?.content);
      console.log('Post URL:', response?.url);
      console.log('Post summary:', response?.summary);
      console.log('Post image_analysis:', response?.image_analysis);
      console.log('Post website_crawl:', response?.website_crawl);
      console.log('Post threat_indicators:', response?.threat_indicators);
      setPost(response);
    } catch (err) {
      console.error('Failed to load post details:', err);
      setError(`Failed to load analysis details: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisSteps = async () => {
    if (!analysisId) return;

    try {
      setStepsLoading(true);
      console.log('Loading analysis steps for analysis ID:', analysisId);
      const response = await api.getAnalysisStepsByAnalysisId(analysisId);
      console.log('Analysis steps response:', response);

      // Transform API response to match AnalysisSteps component format
      const transformedSteps = (response.steps || []).map((step: any) => {
        let stepData: any = {};
        try {
          stepData = typeof step.step_data === 'string' ? JSON.parse(step.step_data) : step.step_data;
        } catch (e) {
          console.error('Failed to parse step data:', e);
          stepData = {};
        }

        // Map step types to component expected types
        const typeMapping: Record<string, string> = {
          'relevance_check': 'relevance',
          'threat_assessment': 'ai_analysis',
          'ioc_extraction': 'entity_extraction',
          'alert_generation': 'threat_detection',
          'enhanced_ai': 'enhanced_ai'
        };

        const mappedType = typeMapping[step.step_type] || step.step_type || 'ai_analysis';

        // Transform data based on step type
        let extractedData = null;
        switch (step.step_type) {
          case 'relevance_check':
            extractedData = {
              score: stepData.score || 0,
              keywords: stepData.keywords || []
            };
            break;
          case 'threat_assessment':
            extractedData = {
              threat_level: stepData.threat_level || 'low',
              confidence: stepData.confidence || 0,
              threat_types: stepData.categories || [],
              iocs: stepData.iocs_found ? [
                { type: 'domain', value: stepData.domain },
                { type: 'ip', value: stepData.ip },
                { type: 'hash', value: stepData.file_hash },
                { type: 'hash', value: stepData.malware_name }
              ].filter(ioc => ioc.value) : []
            };
            break;
          case 'ioc_extraction':
            extractedData = {
              ips: stepData.types?.includes('ip') ? [stepData.ip].filter(Boolean) : [],
              domains: stepData.types?.includes('domain') ? [stepData.domain].filter(Boolean) : [],
              hashes: [stepData.file_hash, stepData.malware_hash].filter(Boolean)
            };
            break;
          case 'alert_generation':
            extractedData = {
              alert_created: stepData.alert_created,
              alert_id: stepData.alert_id,
              severity: stepData.severity
            };
            break;
          case 'enhanced_ai':
            extractedData = {
              threat_level: stepData.threat_level || 'low',
              confidence: stepData.confidence || 0,
              threat_types: stepData.threat_types || [],
              attribution: stepData.attribution,
              ttps: stepData.ttps || [],
              iocs: stepData.iocs || []
            };
            break;
          default:
            extractedData = stepData;
        }

        return {
          id: step.id,
          type: mappedType,
          name: getStepName(step.step_type),
          status: step.status || 'completed',
          timestamp: step.created_at,
          duration: step.processing_time,
          details: {
            model: step.ai_model,
            confidence: stepData.confidence,
            score: stepData.score,
            extractedData: extractedData,
            error: step.error_message,
            metadata: {
              provider: step.ai_provider,
              ...stepData
            }
          }
        };
      });

      console.log('Transformed steps:', transformedSteps);
      setAnalysisSteps(transformedSteps);
    } catch (err) {
      console.error('Failed to load analysis steps:', err);
      setAnalysisSteps([]); // Set empty array on error
    } finally {
      setStepsLoading(false);
    }
  };

  const getStepName = (stepType: string): string => {
    const stepNames: Record<string, string> = {
      'relevance_check': 'Relevance Check',
      'threat_assessment': 'Threat Assessment',
      'ioc_extraction': 'IOC Extraction',
      'alert_generation': 'Alert Generation',
      'ai_analysis': 'AI Analysis',
      'enhanced_ai': 'Enhanced AI Analysis',
      'web_crawl': 'Website Crawl',
      'image_ocr': 'Image OCR',
      'url_extraction': 'URL Extraction',
      'entity_extraction': 'Entity Extraction',
      'threat_detection': 'Threat Detection'
    };
    return stepNames[stepType] || stepType;
  };

  const renderContent = () => {
    if (!post) {
      console.log('No post data available for rendering content');
      return <div className="text-center py-8 text-gray-500">No post data available</div>;
    }

    console.log('Rendering content for tab:', activeTab, 'Post data keys:', Object.keys(post));

    switch (activeTab) {
      case 'summary':
        return (
          <div className="space-y-6">
            {post.summary ? (
              <div className={`p-4 rounded-lg ${post.analysis_type === 'quick_assessment' ? 'bg-amber-50' : 'bg-blue-50'
                }`}>
                <h3 className={`font-medium mb-2 flex items-center space-x-2 ${post.analysis_type === 'quick_assessment' ? 'text-amber-900' : 'text-blue-900'
                  }`}>
                  {post.analysis_type === 'quick_assessment' ? (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Quick Assessment Summary</span>
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4" />
                      <span>Enhanced AI Analysis Summary</span>
                    </>
                  )}
                </h3>
                <p className={
                  post.analysis_type === 'quick_assessment' ? 'text-amber-900' : 'text-blue-800'
                }>{post.summary}</p>
              </div>
            ) : (
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-500">No AI analysis summary available</p>
              </div>
            )}

            {/* TTPs Section */}
            {post.ttps && post.ttps.length > 0 && (
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-medium text-orange-900 mb-2">Tactics, Techniques & Procedures (TTPs)</h3>
                <ul className="space-y-1">
                  {post.ttps.map((ttp, index) => (
                    <li key={index} className="text-orange-800 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{ttp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* IOCs Section */}
            {post.iocs && post.iocs.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-medium text-red-900 mb-2">Indicators of Compromise (IOCs)</h3>
                <ul className="space-y-1">
                  {post.iocs.map((ioc, index) => (
                    <li key={index} className="text-red-800 font-mono text-sm flex items-start">
                      <span className="mr-2">•</span>
                      <span>{ioc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations Section */}
            {post.recommendations && post.recommendations.length > 0 && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-medium text-green-900 mb-2">Security Recommendations</h3>
                <ul className="space-y-1">
                  {post.recommendations.map((rec, index) => (
                    <li key={index} className="text-green-800 flex items-start">
                      <span className="mr-2">✓</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'content':
        return (
          <div className="space-y-6">
            {post.url ? (
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h3 className="font-medium text-indigo-900 mb-2 flex items-center space-x-2">
                  <ExternalLink className="w-4 h-4" />
                  <span>Original Source</span>
                </h3>
                <a
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-700 hover:text-indigo-900 hover:underline break-all"
                >
                  {post.url}
                </a>
              </div>
            ) : (
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-500">No URL available</p>
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Full Content</h3>
              {post.content ? (
                <p className="text-gray-700 whitespace-pre-line break-words">{post.content.trim()}</p>
              ) : (
                <p className="text-gray-500 italic">No content available</p>
              )}
            </div>
          </div>
        );

      case 'images':
        return (
          <div className="space-y-4">
            {post.image_analysis && post.image_analysis.length > 0 ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {post.stored_images?.map((img, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      <img
                        src={img.url}
                        alt={`Image ${index + 1}`}
                        className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(img.url)}
                      />
                      {img.analysis && (
                        <div className="p-3 space-y-2 text-sm">
                          {img.analysis.threat_score > 0 && (
                            <div className="flex items-center space-x-2 text-red-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span>Threat Score: {img.analysis.threat_score}</span>
                            </div>
                          )}
                          {img.analysis.extracted_text && (
                            <div className="bg-gray-100 p-2 rounded text-xs">
                              <strong>Extracted Text:</strong>
                              <p className="mt-1">{img.analysis.extracted_text}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>No images found for this post</p>
              </div>
            )}
          </div>
        );

      case 'website':
        return (
          <div className="space-y-4">
            {post.website_crawl ? (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{post.website_crawl.title || 'Website Data'}</h3>
                    <a
                      href={post.website_crawl.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>{post.website_crawl.url}</span>
                    </a>
                  </div>
                  {post.website_crawl.threat_score > 0 && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm">Threat Score: {post.website_crawl.threat_score}</span>
                    </div>
                  )}
                </div>

                {post.website_crawl.description && (
                  <p className="text-gray-600 text-sm mb-3">{post.website_crawl.description}</p>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status Code:</span>
                    <span className="ml-2 font-medium">{post.website_crawl.status_code || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Response Time:</span>
                    <span className="ml-2 font-medium">
                      {post.website_crawl.response_time ? `${post.website_crawl.response_time}ms` : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Images Found:</span>
                    <span className="ml-2 font-medium">{post.website_crawl.images_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Links Found:</span>
                    <span className="ml-2 font-medium">{post.website_crawl.links_count}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Globe className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>No website data available for this post</p>
              </div>
            )}
          </div>
        );

      case 'indicators':
        return (
          <div className="space-y-4">
            {post.threat_indicators && post.threat_indicators.length > 0 ? (
              <div className="space-y-3">
                {post.threat_indicators.map((indicator, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-gray-900 capitalize">
                            {indicator.indicator_type.replace('_', ' ')}
                          </span>
                          {indicator.verified && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                              Verified
                            </span>
                          )}
                        </div>
                        <code className="text-sm bg-gray-800 text-gray-100 px-2 py-1 rounded">
                          {indicator.indicator_value}
                        </code>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm text-gray-500">Confidence</div>
                        <div className="font-medium">
                          {Math.round(indicator.confidence_score * 100)}%
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>No threat indicators found</p>
              </div>
            )}
          </div>
        );

      case 'stix':
        return <STIXExport post={post} analysisSteps={analysisSteps} />;

      case 'analysis':
        return (
          <div>
            {stepsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading analysis steps...</p>
              </div>
            ) : analysisSteps && analysisSteps.length > 0 ? (
              <AnalysisSteps postId={post!.id} steps={analysisSteps} />
            ) : post!.analysis_type === 'quick_assessment' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-amber-100 p-4 rounded-full mb-4">
                  <Zap className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Quick Assessment Only</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  This post has undergone an initial rapid assessment.
                  {post!.threat_level === 'low' || post!.threat_level === 'unknown'
                    ? ' Based on the low threat level, deep analysis steps were not triggered.'
                    : ' Detailed analysis steps are not available.'}
                </p>
                {post!.summary && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-2xl mx-auto text-left">
                    <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Assessment Result
                    </h4>
                    <p className="text-amber-800 text-sm">{post!.summary}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Activity className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg mb-2">No analysis steps available</p>
                <p className="text-sm">Analysis steps will appear here once the post is processed</p>
              </div>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analysis details...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 mb-4 text-lg">{error || 'Analysis not found'}</div>
          <button
            onClick={() => navigate('/analyzed-posts')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Analysis Results
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/analyzed-posts')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Analysis Results</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-900">Analysis Details</h1>
              {(post.analyzed_steps?.analysis_timestamp || post.analyzed_at) && (
                <>
                  <div className="h-6 w-px bg-gray-300"></div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">
                      Analyzed: {formatAbsoluteTime(post.analyzed_steps?.analysis_timestamp || post.analyzed_at || '')}
                    </span>
                  </div>
                </>
              )}
            </div>
            {post.source_feed && (
              <SourceBadge source_feed={post.source_feed} username={post.username} size="md" />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Post Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-sm bg-blue-100 px-3 py-1 rounded-full text-blue-700">
              {formatPostIdForDisplay(post.id)}
            </span>

            {post.latest_alert_id && (
              <span
                className="font-mono text-sm bg-red-100 px-3 py-1 rounded-full text-red-700 cursor-pointer hover:bg-red-200 transition-colors"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.append('alert_id', post.latest_alert_id!);
                  navigate(`/alerts?${params.toString()}`);
                }}
                title="Click to view alert details"
              >
                Alert: {post.latest_alert_id}
              </span>
            )}

            {post.analysis_id && (
              <span className="font-mono text-sm bg-purple-100 px-3 py-1 rounded-full text-purple-700">
                Analysis: {post.analysis_id}
              </span>
            )}

            <span className="text-gray-600">@{post.username}</span>

            <span className="text-gray-500">
              {formatAbsoluteTime(post.created_at)}
            </span>

            <span className={`px-3 py-1 rounded-full text-sm font-medium ${post.threat_level === 'critical' ? 'bg-red-100 text-red-800' :
              post.threat_level === 'high' ? 'bg-orange-100 text-orange-800' :
                post.threat_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  post.threat_level === 'low' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
              }`}>
              {post.threat_level.toUpperCase()}
            </span>

            {/* Analysis Type Badge */}
            {post.analysis_type === 'quick_assessment' ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 flex items-center space-x-1 border border-amber-200">
                <Zap className="w-4 h-4" />
                <span>Quick Assessment</span>
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 flex items-center space-x-1 border border-purple-200">
                <Brain className="w-4 h-4" />
                <span>Enhanced Analysis</span>
              </span>
            )}

            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium flex items-center gap-1">
                <FileText className="w-4 h-4" />
                <span>Text</span>
              </span>

              {post.image_analysis && post.image_analysis.length > 0 && (
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <ImageIcon className="w-4 h-4" />
                  <span>Images ({post.image_analysis.length})</span>
                </span>
              )}

              {post.website_crawl && (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <Globe className="w-4 h-4" />
                  <span>Web Data</span>
                </span>
              )}
            </div>

            {post.url && (
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline"
                title="View original post"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="truncate max-w-xs">{post.url}</span>
              </a>
            )}
          </div>
        </div>

        {/* Depth Analysis Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6 border-b bg-purple-50">
            <h2 className="text-lg font-semibold text-purple-900 mb-4 flex items-center space-x-2">
              <Layers className="w-5 h-5" />
              <span>Depth Analysis Results</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((level) => {
                const isAvailable = level === 0 || (post.depth_max_level !== undefined && post.depth_max_level >= level);
                const isSelected = selectedDepthLevel === level;

                return (
                  <button
                    key={level}
                    onClick={() => {
                      console.log(`[PostDetails] Depth level ${level} button clicked for analysisId=${analysisId}`);
                      if (isAvailable) {
                        setSelectedDepthLevel(level);
                      }
                    }}
                    disabled={!isAvailable}
                    className={`flex flex-col items-center p-4 rounded-lg border transition-all ${isSelected
                      ? 'bg-purple-100 border-purple-500 ring-2 ring-purple-300' // Selected state
                      : isAvailable
                        ? 'bg-white border-purple-200 hover:border-purple-400 hover:bg-purple-50' // Available but not selected
                        : 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60' // Not available
                      }`}
                  >
                    <div className={`text-3xl font-bold mb-2 ${isSelected
                      ? 'text-purple-800' // Selected
                      : isAvailable
                        ? 'text-purple-600' // Available
                        : 'text-gray-400' // Not available
                      }`}>
                      L{level}
                    </div>
                    <div className={`text-sm text-center mb-2 ${isSelected
                      ? 'text-purple-700' // Selected
                      : isAvailable
                        ? 'text-gray-600' // Available
                        : 'text-gray-400' // Not available
                      }`}>
                      {level === 0 && 'Original Post'}
                      {level === 1 && 'Direct URLs'}
                      {level === 2 && 'Nested URLs'}
                      {level === 3 && 'Deep Analysis'}
                    </div>
                    {isAvailable && (
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-purple-500' : 'bg-green-500'
                          }`}></div>
                        <span className={`text-xs ${isSelected ? 'text-purple-600' : 'text-green-600'
                          }`}>
                          {level === 0 && isSelected ? 'Current View' : 'Available'}
                        </span>
                      </div>
                    )}
                    {!isAvailable && level > 0 && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <span className="text-xs text-gray-400">Not Available</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {post.depth_analysis_complete && (
              <div className="mt-4 text-sm text-purple-700 flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Complete depth analysis finished</span>
                {post.depth_aggregated_threat_score !== undefined && (
                  <span className="ml-auto font-medium">
                    Aggregated Score: {(post.depth_aggregated_threat_score * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs and Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Tab Navigation */}
          <div className="border-b">
            <nav className="flex space-x-8 px-6 overflow-x-auto scrollbar-hide" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('summary')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Summary</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('content')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'content'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Content</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('images')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'images'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <ImageIcon className="w-4 h-4" />
                  <span>Images</span>
                  {post.image_analysis && post.image_analysis.length > 0 && (
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                      {post.image_analysis.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('website')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'website'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4" />
                  <span>Website Data</span>
                  {post.website_crawl && (
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                      1
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('indicators')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'indicators'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Threat Indicators</span>
                  {post.threat_indicators && post.threat_indicators.length > 0 && (
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                      {post.threat_indicators.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('stix')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'stix'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Code className="w-4 h-4" />
                  <span>STIX Export</span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                    Latest
                  </span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('analysis')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'analysis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4" />
                  <span>AI Analysis Steps</span>
                  {analysisSteps.length > 0 && (
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                      {analysisSteps.length}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Depth Analysis Modal */}
      {selectedDepthLevel !== null && selectedDepthLevel !== 0 && (
        <DepthAnalysisModal
          postId={post.id}
          depthLevel={selectedDepthLevel}
          onClose={() => setSelectedDepthLevel(0)} // Reset to L0 instead of null
        />
      )}
    </div>
  );
}